import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Stack, Divider } from "@mui/material";
import { motion } from "framer-motion";

const TermsOfServicePage = () => {
  const { t } = useTranslation();

  const lastUpdated = new Date().toLocaleDateString(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const scopeItems = t("legal.terms.scope.items", { returnObjects: true });
  const conductItems = t("legal.terms.conduct.items", { returnObjects: true });
  const newsletterItems = t("legal.terms.newsletter.items", { returnObjects: true });

  return (
    <Box component="main" sx={{ bgcolor: "#0B0F14", minHeight: "100vh", pb: 20 }}>
      <Box sx={{ py: 15, bgcolor: "#111827", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Typography
              variant="overline"
              align="center"
              sx={{ color: "#0FA3A3", fontWeight: 700, letterSpacing: 3, display: "block", mb: 2 }}
            >
              {t("legal.terms.lastUpdatedLabel").toUpperCase()} — {lastUpdated}
            </Typography>
            <Typography
              variant="h1"
              align="center"
              sx={{ color: "white", mb: 3, fontFamily: "Philosopher", fontSize: { xs: "2.25rem", md: "3.25rem" } }}
            >
              {t("legal.terms.title")}
            </Typography>
            <Typography
              variant="h6"
              align="center"
              sx={{ color: "#9CA3AF", fontWeight: 300, lineHeight: 1.6, maxWidth: "760px", mx: "auto" }}
            >
              {t("legal.terms.intro")}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 10 }}>
        <Stack spacing={6}>
          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.scope.title")}
            </Typography>
            <Stack spacing={1}>
              {scopeItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.access.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.access.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.conduct.title")}
            </Typography>
            <Stack spacing={1}>
              {conductItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.ip.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.ip.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.userContent.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.userContent.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.newsletter.title")}
            </Typography>
            <Stack spacing={1}>
              {newsletterItems.map((item, idx) => (
                <Typography key={idx} variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
                  {item}
                </Typography>
              ))}
            </Stack>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.external.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.external.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.liability.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.liability.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.law.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.law.text")}
            </Typography>
          </Box>

          <Divider sx={{ borderColor: "rgba(255,255,255,0.05)" }} />

          <Box>
            <Typography variant="h4" sx={{ color: "white", mb: 2, fontFamily: "Philosopher", fontWeight: 700 }}>
              {t("legal.terms.changes.title")}
            </Typography>
            <Typography variant="body1" sx={{ color: "#9CA3AF", lineHeight: 1.8 }}>
              {t("legal.terms.changes.text")}
            </Typography>
          </Box>

          <Box sx={{ p: 4, bgcolor: "rgba(212, 175, 55, 0.05)", borderRadius: "20px", border: "1px solid rgba(212, 175, 55, 0.15)" }}>
            <Typography variant="body2" sx={{ color: "#D4AF37", lineHeight: 1.8, fontWeight: 700 }}>
              {t("legal.terms.disclaimer")}
            </Typography>
          </Box>
        </Stack>
      </Container>
    </Box>
  );
};

export default TermsOfServicePage;
